// ET sound scripts (sound/scripts/*.sounds, cg_sound.c): named sounds that
// pick one of several files, used by the "noise" keys of map entities in
// place of a file name. The scripts to load are listed in
// sound/scripts/filelist.txt, plus <map>.sounds of the current map.

import { tokenize } from './tokenize';

export interface SoundScript {
  name: string;
  /** Files to choose from (one block = one group; CG_SoundPickOldestRandomSound picks the oldest group). */
  groups: string[][];
  /** No distance attenuation ("global"). */
  global: boolean;
  looping: boolean;
  streaming: boolean;
}

/** CG_SoundParseSounds: every named sound of a script (later definitions of a name are added to the front). */
export function parseSoundScript(text: string): SoundScript[] {
  const out: SoundScript[] = [];
  const tokens = tokenize(text);
  let current: SoundScript | null = null;
  let group: string[] | null = null;
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i].text;
    const lower = t.toLowerCase();
    if (lower === '{') {
      if (!current || group) throw new Error(`sound script: unexpected '{'${current ? ` in ${current.name}` : ''}`);
      group = [];
      continue;
    }
    if (lower === '}') {
      if (!current || !group) throw new Error(`sound script: unexpected '}'`);
      current.groups.unshift(group);
      out.push(current);
      current = null;
      group = null;
      continue;
    }
    if (!group) {
      // the name of a new sound
      if (current) throw new Error(`sound script: '${t}' unexpected after ${current.name}`);
      current = { name: t, groups: [], global: false, looping: false, streaming: false };
      continue;
    }
    switch (lower) {
      case 'global':
        current!.global = true;
        break;
      case 'looping':
        current!.looping = true;
        break;
      case 'streaming':
        current!.streaming = true;
        break;
      case 'sound':
        if (i + 1 < tokens.length) group.push(tokens[++i].text.replace(/\\/g, '/').toLowerCase());
        break;
      // channel names and anything else only matter to ET's mixer
    }
  }
  if (current || group) throw new Error('sound script: no concluding }');
  return out;
}

/** Script files ET loads for a map (filelist.txt entries, then <map>.sounds). */
export function soundScriptFiles(filelist: string | null, map: string): string[] {
  const names = filelist ? tokenize(filelist).map((t) => t.text) : [];
  names.push(`${map}.sounds`);
  return names.map((n) => `sound/scripts/${n.toLowerCase()}`);
}

/** What loadSoundScripts reads files with (a VirtualFS). */
export interface TextSource {
  readText(path: string): Promise<string | null>;
  list(dir: string, ext: string): string[];
}

/**
 * The sound scripts a map can use: ET's (filelist.txt + <map>.sounds), then
 * any other script in the source (a map bundle carries only the scripts its
 * sounds come from). Broken scripts are skipped.
 */
export async function loadSoundScripts(fs: TextSource, map: string): Promise<{ scripts: SoundScripts; files: string[] }> {
  const scripts = new SoundScripts();
  const files: string[] = [];
  const paths = soundScriptFiles(await fs.readText('sound/scripts/filelist.txt'), map);
  for (const p of fs.list('sound/scripts', '.sounds')) if (!paths.includes(p)) paths.unshift(p);
  for (const path of paths) {
    const text = await fs.readText(path);
    if (!text) continue;
    try {
      scripts.add(parseSoundScript(text));
      files.push(path);
    } catch {
      // ET stops with an error; a broken map script just has no sounds here
    }
  }
  return { scripts, files };
}

/** Sound scripts by lower-case name (the last definition of a name wins). */
export class SoundScripts {
  private readonly byName = new Map<string, SoundScript>();

  add(scripts: SoundScript[]): void {
    for (const s of scripts) this.byName.set(s.name.toLowerCase(), s);
  }

  get(name: string): SoundScript | undefined {
    return this.byName.get(name.toLowerCase());
  }

  get size(): number {
    return this.byName.size;
  }
}

/**
 * The sound files a "noise" value can play: a script's files, or the value
 * as a file name (".wav" added when it has no extension).
 */
export function noiseFiles(noise: string, scripts: SoundScripts): string[] {
  const script = scripts.get(noise);
  if (script) return script.groups.flat();
  const path = noise.replace(/\\/g, '/').toLowerCase();
  if (!path || path === 'nosound') return [];
  return [/\.(wav|ogg|mp3)$/.test(path) ? path : `${path}.wav`];
}
